import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

/**
 * Inline editable remarks cell.
 * - Empty → "+ Add remark"
 * - Filled → truncated text (50 chars) with tooltip
 * - Click → opens inline textarea; Enter saves, Shift+Enter newline, Esc cancels, blur saves.
 */
export default function RemarksCell({ tender, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(tender.remarks || "");
  const [saving, setSaving] = useState(false);
  const taRef = useRef(null);
  const initialRef = useRef(tender.remarks || "");

  useEffect(() => {
    setValue(tender.remarks || "");
    initialRef.current = tender.remarks || "";
  }, [tender.id, tender.remarks]);

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length, taRef.current.value.length);
    }
  }, [editing]);

  const commit = async () => {
    if (saving) return;
    const trimmed = (value || "").slice(0, 500);
    if (trimmed === initialRef.current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenders/${tender.id}/remarks`, { remarks: trimmed });
      initialRef.current = trimmed;
      onUpdated?.(data);
      setEditing(false);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  };

  const cancel = () => {
    setValue(initialRef.current);
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <div className="relative" data-testid={`remarks-editing-${tender.id}`}>
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, 500))}
          onBlur={commit}
          onKeyDown={onKeyDown}
          maxLength={500}
          rows={Math.min(4, Math.max(2, value.split("\n").length))}
          className="w-full min-w-[200px] px-2 py-1.5 text-xs border border-brand bg-white rounded-sm focus:outline-none focus:ring-2 focus:ring-brand/20"
          data-testid={`remarks-textarea-${tender.id}`}
        />
        <div className="text-[10px] text-ink-muted mt-1 flex items-center gap-2 font-num">
          <span>{value.length}/500</span>
          <span className="text-ink-muted">Enter to save · Shift+Enter for newline · Esc to cancel</span>
        </div>
      </div>
    );
  }

  if (!tender.remarks) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-[12px] text-ink-muted hover:text-brand inline-flex items-center gap-1 transition-colors group"
        data-testid={`remarks-add-${tender.id}`}
      >
        <Plus className="w-3 h-3 opacity-60 group-hover:opacity-100" /> Add remark
      </button>
    );
  }

  const display = tender.remarks.length > 50 ? tender.remarks.slice(0, 50) + "…" : tender.remarks;
  const tooltip = tender.remarks_updated_by_name
    ? `${tender.remarks}\n— ${tender.remarks_updated_by_name}${tender.remarks_updated_at ? " · " + new Date(tender.remarks_updated_at).toLocaleString() : ""}`
    : tender.remarks;

  return (
    <button
      onClick={() => setEditing(true)}
      title={tooltip}
      className="text-left text-sm hover:text-brand transition-colors max-w-[220px] truncate cursor-text"
      data-testid={`remarks-display-${tender.id}`}
    >
      {display}
    </button>
  );
}
